import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ContactButton from '../../../components/candidates/ContactButton';
import { supabase } from '../../../utils/supabase.js';
import { calculateSkillsMatch } from '../../../utils/skillsUtils';
import { formatArrayForDisplay } from '../../../utils/arrayNormalization';

const PREMIUM_TIERS = ['pro', 'premium', 'agency'];
const DEFAULT_REQUIRED_SKILLS = ['Administrative Support', 'Customer Service', 'Data Entry'];

/** Always returns a string — guards against truthy non-string JSONB values (e.g. {}) */
const safeStr = (val, fallback = '') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string') return val || fallback;
  if (typeof val === 'number') return String(val);
  return fallback;
};

/** Safely extracts a skill label from a skill value that may be a string or object */
const skillLabel = (skill) => {
  if (typeof skill === 'string') return skill;
  if (skill && typeof skill === 'object') {
    const name = skill.name || skill.label || skill.type;
    return typeof name === 'string' ? name : 'Unknown Skill';
  }
  return 'Unknown Skill';
};

/**
 * CandidateCard component for displaying individual candidate information
 * Optimized with React.memo to prevent unnecessary re-renders
 */
const CandidateCard = React.memo(({ candidate, viewMode, user, savedCandidates, onSaveCandidate }) => {
  const navigate = useNavigate();

  // Calculate skills match with error handling
  const skillsMatchScore = useMemo(() => {
    try {
      if (!candidate?.skills || !Array.isArray(candidate.skills)) return 0;
      
      const requiredSkills = user?.preferences?.preferredSkills || DEFAULT_REQUIRED_SKILLS;
      
      // Extract just the skill names from candidate skills array
      const candidateSkillNames = candidate.skills.map(skill => 
        typeof skill === 'string' ? skill : (skill.name || '')
      );
      
      return calculateSkillsMatch(candidateSkillNames, requiredSkills).score || 0;
    } catch {
      return 0;
    }
  }, [candidate?.skills, user?.preferences?.preferredSkills]);

  const displayName = safeStr(candidate?.full_name, null) || safeStr(candidate?.name, null) || 'Unknown VA';
  const displayTitle = safeStr(candidate?.title, null) || safeStr(candidate?.job_title, null) || 'Virtual Assistant';
  const displayLocation = safeStr(candidate?.location, 'Not specified');
  const displayRate = candidate?.hourly_rate
    ? `$${candidate.hourly_rate}/hr at $${candidate.hourly_rate * 160}/month`
    : 'Rate not specified';
  const displayRating = typeof candidate?.average_rating === 'number' ? candidate.average_rating : 0;
  const displayExperience = typeof candidate?.experience_years === 'number' ? candidate.experience_years : 0;
  const displayBio = safeStr(candidate?.bio, 'No bio available');
  const displayAvailability = safeStr(candidate?.availability, null) || safeStr(candidate?.availability_status, 'Not specified');
  const displayPreferredProjectTypes = useMemo(() => {
    return formatArrayForDisplay(candidate?.preferred_project_types, ', ', 'N/A');
  }, [candidate?.preferred_project_types]);
  const isSaved = savedCandidates.includes(candidate.id);
  
  const hasPremiumTier = useMemo(() => {
    return user?.subscription_tier && PREMIUM_TIERS.includes(user.subscription_tier);
  }, [user?.subscription_tier]);

  const ProfilePicture = useMemo(() => (
    <div className="w-24 h-24 rounded-full bg-secondary-200 flex items-center justify-center overflow-hidden">
      {candidate?.profile_picture ? (
        <img 
          src={candidate.profile_picture} 
          alt={displayName}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <Icon name="User" size={24} className="text-secondary-500" />
      )}
    </div>
  ), [candidate?.profile_picture, displayName]);

  const SkillBadges = useMemo(() => {
    if (!candidate?.skills || !Array.isArray(candidate.skills)) return null;
    
    return candidate.skills.slice(0, 3).map((skill, index) => (
      <span 
        key={`${candidate.id}-skill-${index}`}
        className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-md"
      >
        {skillLabel(skill)}
      </span>
    ));
  }, [candidate?.skills, candidate.id]);

  // Handle profile view - navigate to public VA profile
  const handleViewProfile = async () => {
    if (!candidate.id) return;

    trackActivity('candidate_viewed', {
      matched_skills: candidate.skills
        ? candidate.skills.slice(0, 5).map(s => typeof s === 'string' ? s : s.name)
        : []
    });

    if (candidate.public_username) {
      navigate(`/va/${candidate.public_username}`);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('public_username')
        .eq('id', candidate.id)
        .single();

      if (!error && data?.public_username) {
        navigate(`/va/${data.public_username}`);
        return;
      }
    } catch {
      // Fall through to lookup
    }

    navigate(`/va-lookup/${candidate.id}`);
  };

  // Track profile view or message activity (fire-and-forget)
  const trackActivity = (activityType, metadata = {}) => {
    if (!user?.id) return;

    supabase.from('employer_activities').insert({
      employer_id: user.id,
      activity_type: activityType,
      reference_id: candidate.id,
      reference_type: 'user_profile',
      description: `${activityType === 'message_sent' ? 'Message sent to' : 'Viewed candidate'} ${displayName}`,
      metadata: { source: 'candidate_search', ...metadata }
    }).then(({ error }) => {
      if (error) console.warn('Activity tracking failed:', error.message);
    }).catch(() => {});
  };

  // Derived flags for video intro and AI interview
  const hasVideoIntro = !!candidate?.video_introduction_url;
  const hasAiInterview = Array.isArray(candidate?.ai_interview_sessions) &&
    candidate.ai_interview_sessions.some(s => s.status === 'completed' && s.is_public);

  if (viewMode === 'list') {
    return (
      <div 
        className="relative bg-surface rounded-lg border border-border p-4 hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer"
        onClick={handleViewProfile}
      >
        {/* Bookmark ribbon for list view - absolutely positioned at top-right */}
        <button
          onClick={(e) => { e.stopPropagation(); onSaveCandidate(candidate.id); }}
          title={isSaved ? 'Remove from saved' : 'Save candidate'}
          className="absolute top-0 right-5 z-10 focus:outline-none group"
          style={{ transform: 'translateY(-2px)' }}
        >
          <div
            className={`w-7 h-10 flex items-center justify-center transition-all duration-200 select-none shadow-md ${
              isSaved
                ? 'bg-red-500 text-white scale-105'
                : 'bg-secondary-300 text-secondary-500 group-hover:bg-red-400 group-hover:text-white'
            }`}
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 82%, 0 100%)' }}
          >
            {isSaved && <Icon name="Check" size={12} />}
          </div>
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {ProfilePicture}
            <div className="flex-1">
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <h3 className="text-lg font-semibold text-text-primary">{displayName}</h3>
                {candidate?.identity_verification_status === 'verified' && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold"
                    title="Identity Verified"
                  >
                    <Icon name="ShieldCheck" size={11} />
                    ID Verified
                  </span>
                )}
              </div>
              <p className="text-text-secondary">{displayTitle}</p>
              <div className="flex items-center space-x-4 mt-1 text-sm text-text-muted">
                <span className="flex items-center">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  {displayLocation}
                </span>
                <span className="flex items-center">
                  <Icon name="DollarSign" size={14} className="mr-1" />
                  {displayRate}
                </span>
                <span className="flex items-center">
                  <Icon name="Clock" size={14} className="mr-1" />
                  {displayExperience} years
                </span>
                {displayRating > 0 && (
                  <span className="flex items-center">
                    <Icon name="Star" size={14} className="mr-1 text-warning" />
                    {displayRating.toFixed(1)}
                  </span>
                )}
              </div>
              <div className="flex items-center mt-1 text-sm text-text-muted">
                <span className="flex items-center">
                  <Icon name="Calendar" size={14} className="mr-1" />
                  Status: {displayAvailability}
                </span>
              </div>
              <div className="flex items-center mt-1 text-sm text-text-muted">
                <span className="flex items-center">
                  <Icon name="Briefcase" size={14} className="mr-1" />
                  Looking For: {displayPreferredProjectTypes}
                </span>
              </div>
              {skillsMatchScore > 0 && (
                <div className="flex items-center text-sm text-text-muted">
                  <Icon name="Target" size={14} className="mr-2 text-primary" />
                  <span>{Math.round(skillsMatchScore)}% skills match</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {SkillBadges}
                {candidate?.skills && candidate.skills.length > 3 && (
                  <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md">
                    +{candidate.skills.length - 3} more
                  </span>
                )}
              </div>
              {(hasVideoIntro || hasAiInterview) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {hasVideoIntro && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
                      <Icon name="Video" size={11} />
                      Video Intro
                    </span>
                  )}
                  {hasAiInterview && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-medium">
                      <Icon name="Bot" size={11} />
                      AI Interviewed
                    </span>
                  )}
                </div>
              )}
              <div className="mt-2 text-sm text-text-secondary line-clamp-2">
                {displayBio}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 pl-4" onClick={(e) => e.stopPropagation()}>
            {hasPremiumTier && (
              <ContactButton
                vaId={candidate.id}
                vaName={displayName}
                onMessageSent={(messageData) => {
                  if (window.toastify?.success) {
                    window.toastify.success(`Message sent to ${displayName}`);
                  }
                  trackActivity('message_sent', {
                    message_id: messageData?.id || null,
                    conversation_id: messageData?.conversation_id || null
                  });
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div 
      className="relative bg-surface rounded-lg border border-border p-6 hover:border-primary hover:shadow-lg transition-all duration-200 cursor-pointer overflow-visible"
      onClick={handleViewProfile}
    >
        {/* Bookmark ribbon attached to top-right corner */}
        <button
          onClick={(e) => { e.stopPropagation(); onSaveCandidate(candidate.id); }}
          title={isSaved ? 'Remove from saved' : 'Save candidate'}
          className="absolute top-0 right-5 z-10 focus:outline-none group"
          style={{ transform: 'translateY(-2px)' }}
        >
          <div
            className={`w-9 h-12 flex items-center justify-center transition-all duration-200 select-none shadow-md ${
              isSaved
                ? 'bg-red-500 text-white scale-105'
                : 'bg-secondary-300 text-secondary-500 group-hover:bg-red-400 group-hover:text-white'
            }`}
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 83%, 0 100%)' }}
          >
            {isSaved && <Icon name="Check" size={14} className="mt-0.5" />}
          </div>
        </button>

        <div className="flex items-start mb-4">
          <div className="flex items-center space-x-3 flex-1 pr-8">
            {ProfilePicture}
            <div>
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <h3 className="text-lg font-semibold text-text-primary">{displayName}</h3>
                {candidate?.identity_verification_status === 'verified' && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold"
                    title="Identity Verified"
                  >
                    <Icon name="ShieldCheck" size={11} />
                    ID Verified
                  </span>
                )}
              </div>
              <p className="text-text-secondary">{displayTitle}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 gap-4 mb-2">
          <div className="flex items-center text-sm text-text-muted">
            
            <span>{displayRate}</span>
          </div>
          <div className="flex items-center text-sm text-text-muted">
            <Icon name="Clock" size={14} className="mr-2" />
            <span>{displayExperience} years experience</span>
          </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center text-sm text-text-muted">
            <Icon name="Calendar" size={14} className="mr-2" />
            <span>Status: {displayAvailability}</span>
          </div>
          <div className="flex items-center text-sm text-text-muted">
            <Icon name="Briefcase" size={14} className="mr-2" />
            <span>Looking For: {displayPreferredProjectTypes}</span>
          </div>
          </div>
          {displayRating > 0 && (
            <div className="flex items-center text-sm text-text-muted">
              <Icon name="Star" size={14} className="mr-2 text-warning" />
              <span>{displayRating.toFixed(1)} rating</span>
            </div>
          )}
          {skillsMatchScore > 0 && (
            <div className="flex items-center text-sm text-text-muted">
              <Icon name="Target" size={14} className="mr-2 text-primary" />
              <span>{Math.round(skillsMatchScore)}% skills match</span>
            </div>
          )}
        </div>

        {/* Feature banners: Video Introduction & AI Interview */}
        {(hasVideoIntro || hasAiInterview) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {hasVideoIntro && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
                <Icon name="Video" size={11} />
                Video Intro
              </span>
            )}
            {hasAiInterview && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-medium">
                <Icon name="Bot" size={11} />
                AI Interviewed
              </span>
            )}
          </div>
        )}

        <div className="mb-4">
          <h4 className="text-sm font-medium text-text-primary mb-2">About Me:</h4>
          <p className="text-sm text-text-secondary line-clamp-3">
            {displayBio}
          </p>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-medium text-text-primary mb-2">Top Skills</h4>
          <div className="flex flex-wrap gap-1">
            {SkillBadges}
            {candidate?.skills && candidate.skills.length > 3 && (
              <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-md">
                +{candidate.skills.length - 3} more
              </span>
            )}
          </div>
        </div>

        <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
          {hasPremiumTier && (
            <ContactButton
              vaId={candidate.id}
              vaName={displayName}
              onMessageSent={(messageData) => {
                if (window.toastify?.success) {
                  window.toastify.success(`Message sent to ${displayName}`);
                }
                trackActivity('message_sent', {
                  message_id: messageData?.id || null,
                  conversation_id: messageData?.conversation_id || null
                });
              }}
            />
          )}
        </div>
    </div>
  );
});

CandidateCard.displayName = 'CandidateCard';

export default CandidateCard;